import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TablePagination,
  Collapse
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSearchParams } from 'react-router-dom';
import authApi from '../../services/authApi';
import { sharePointService } from '../../services/sharePointService';
import { useUserSession } from '../../contexts/UserContext';
import { useSharePoint } from '../../contexts/SharePointContext';

const HEADER_BG = '#f4511e';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function PrincipalsTableBody({
  loading,
  error,
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  onRowsPerPageChange,
  embedded
}) {
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {total > 0 ? `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}` : 'No rows'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: HEADER_BG }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Permission</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((u, idx) => (
                  <TableRow key={`${u.dedupeKey}-${idx}`}>
                    <TableCell>{u.displayName || u.principalId}</TableCell>
                    <TableCell>{u.email || '—'}</TableCell>
                    <TableCell>{u.permissionLevel || '—'}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={3}>No rows</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={embedded ? { '.MuiTablePagination-toolbar': { flexWrap: 'wrap', pl: 0 } } : undefined}
          />
        </>
      )}
    </>
  );
}

function PrincipalDetailBlock({
  detailCtx,
  loading: detailLoading,
  error: detailError,
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  onRowsPerPageChange,
  emptyHint
}) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Principals
      </Typography>
      {!detailCtx && (
        <Typography variant="body2" color="text.secondary">
          {emptyHint}
        </Typography>
      )}
      {detailCtx && (
        <PrincipalsTableBody
          loading={detailLoading}
          error={detailError}
          rows={rows}
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          embedded={false}
        />
      )}
    </Box>
  );
}

const PermissionScopeReport = () => {
  const { userSession } = useUserSession();
  const { selectedConfig, refreshTrigger } = useSharePoint();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ sites: [], libraries: [], folders: [] });
  const [tab, setTab] = useState(0);

  const [detailCtx, setDetailCtx] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(0);
  const [detailPageSize, setDetailPageSize] = useState(25);

  const [expandedLibrary, setExpandedLibrary] = useState(null);
  const [libPage, setLibPage] = useState(0);
  const [libPageSize, setLibPageSize] = useState(25);
  const [libLoading, setLibLoading] = useState(false);
  const [libError, setLibError] = useState(null);
  const [libRows, setLibRows] = useState([]);
  const [libTotal, setLibTotal] = useState(0);

  const [expandedFolder, setExpandedFolder] = useState(null);
  const [foldPage, setFoldPage] = useState(0);
  const [foldPageSize, setFoldPageSize] = useState(25);
  const [foldLoading, setFoldLoading] = useState(false);
  const [foldError, setFoldError] = useState(null);
  const [foldRows, setFoldRows] = useState([]);
  const [foldTotal, setFoldTotal] = useState(0);

  const scope = useMemo(() => {
    const allSites = searchParams.get('allSites') === '1';
    const raw = searchParams.get('configId');
    const fromQuery = raw != null && raw !== '' ? parseInt(raw, 10) : null;
    const configId = Number.isFinite(fromQuery) && fromQuery > 0 ? fromQuery : null;
    return { allSites, configId };
  }, [searchParams]);

  const effectiveConfigId = scope.allSites ? null : scope.configId ?? selectedConfig;
  /** When one site is in scope, library/folder rows are all for that site — hide redundant Site column. */
  const showSiteColumnInLibrariesAndFolders = scope.allSites;

  useEffect(() => {
    const load = async () => {
      if (!userSession?.email) {
        setLoading(false);
        return;
      }
      if (!scope.allSites && effectiveConfigId == null) {
        setError('Select a site or open this report from Overview with a scope.');
        setLoading(false);
        setData({ sites: [], libraries: [], folders: [] });
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const token = authApi.getToken();
        const payload = await sharePointService.getPermissionScopeReport(token, {
          allSites: scope.allSites,
          configId: scope.allSites ? undefined : effectiveConfigId
        });
        setData({
          sites: payload.sites || [],
          libraries: payload.libraries || [],
          folders: payload.folders || []
        });
      } catch (e) {
        setError(e.message || 'Failed to load permission scope report');
        setData({ sites: [], libraries: [], folders: [] });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userSession?.email, scope.allSites, scope.configId, selectedConfig, refreshTrigger]);

  useEffect(() => {
    if (tab === 1 || tab === 2) {
      setDetailCtx(null);
    }
    if (tab !== 1) {
      setExpandedLibrary(null);
      setLibRows([]);
      setLibTotal(0);
      setLibError(null);
      setLibLoading(false);
    }
    if (tab !== 2) {
      setExpandedFolder(null);
      setFoldRows([]);
      setFoldTotal(0);
      setFoldError(null);
      setFoldLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (tab !== 0 || scope.allSites || loading) return;
    const id = scope.configId ?? selectedConfig;
    if (id == null) return;
    setDetailCtx({ scope: 'site', configId: id });
  }, [tab, scope.allSites, scope.configId, selectedConfig, loading]);

  useEffect(() => {
    if (tab !== 0 || !scope.allSites || loading) return;
    if (!data.sites.length) {
      setDetailCtx(null);
      return;
    }
    setDetailCtx((prev) => {
      if (prev && prev.scope === 'site' && data.sites.some((s) => s.configId === prev.configId)) {
        return prev;
      }
      return { scope: 'site', configId: data.sites[0].configId };
    });
  }, [tab, scope.allSites, loading, data.sites]);

  const fetchDetailPage = useCallback(async (ctx, page, pageSize) => {
    if (!ctx) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const token = authApi.getToken();
      const res = await sharePointService.getPermissionScopeDetail(token, {
        scope: ctx.scope,
        configId: ctx.configId,
        libraryInternalId: ctx.libraryInternalId,
        folderItemId: ctx.folderItemId,
        page,
        pageSize
      });
      setDetailRows(res.users || []);
      setDetailTotal(typeof res.total === 'number' ? res.total : 0);
    } catch (e) {
      setDetailError(e.response?.data?.details || e.message || 'Failed to load detail');
      setDetailRows([]);
      setDetailTotal(0);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 1 || !expandedLibrary) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLibLoading(true);
      setLibError(null);
      try {
        const token = authApi.getToken();
        const res = await sharePointService.getPermissionScopeDetail(token, {
          scope: 'library',
          configId: expandedLibrary.configId,
          libraryInternalId: expandedLibrary.libraryInternalId,
          page: libPage,
          pageSize: libPageSize
        });
        if (cancelled) return;
        setLibRows(res.users || []);
        setLibTotal(typeof res.total === 'number' ? res.total : 0);
      } catch (e) {
        if (cancelled) return;
        setLibError(e.response?.data?.details || e.message || 'Failed to load principals');
        setLibRows([]);
        setLibTotal(0);
      } finally {
        if (!cancelled) setLibLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setLibLoading(false);
    };
  }, [tab, expandedLibrary, libPage, libPageSize]);

  useEffect(() => {
    if (tab !== 2 || !expandedFolder) {
      return;
    }
    let cancelled = false;
    (async () => {
      setFoldLoading(true);
      setFoldError(null);
      try {
        const token = authApi.getToken();
        const res = await sharePointService.getPermissionScopeDetail(token, {
          scope: 'folder',
          configId: expandedFolder.configId,
          libraryInternalId: expandedFolder.libraryInternalId,
          folderItemId: expandedFolder.folderItemId,
          page: foldPage,
          pageSize: foldPageSize
        });
        if (cancelled) return;
        setFoldRows(res.users || []);
        setFoldTotal(typeof res.total === 'number' ? res.total : 0);
      } catch (e) {
        if (cancelled) return;
        setFoldError(e.response?.data?.details || e.message || 'Failed to load principals');
        setFoldRows([]);
        setFoldTotal(0);
      } finally {
        if (!cancelled) setFoldLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setFoldLoading(false);
    };
  }, [tab, expandedFolder, foldPage, foldPageSize]);

  useEffect(() => {
    if (!detailCtx) {
      setDetailRows([]);
      setDetailTotal(0);
      return;
    }
    setDetailPage(0);
    fetchDetailPage(detailCtx, 0, detailPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- page size changes handled in onRowsPerPageChange only
  }, [detailCtx?.scope, detailCtx?.configId, detailCtx?.libraryInternalId, detailCtx?.folderItemId, fetchDetailPage]);

  const handleDetailPageChange = (_, newPage) => {
    setDetailPage(newPage);
    if (detailCtx) fetchDetailPage(detailCtx, newPage, detailPageSize);
  };

  const handleDetailRowsPerPage = (e) => {
    const next = parseInt(e.target.value, 10);
    setDetailPageSize(next);
    setDetailPage(0);
    if (detailCtx) fetchDetailPage(detailCtx, 0, next);
  };

  const selectSiteRow = (row) => {
    setDetailCtx({ scope: 'site', configId: row.configId });
    setDetailPage(0);
  };

  const sameLibrary = (a, b) =>
    a &&
    b &&
    a.configId === b.configId &&
    String(a.libraryInternalId) === String(b.libraryInternalId);

  const sameFolder = (a, b) =>
    sameLibrary(a, b) && a && b && String(a.folderItemId) === String(b.folderItemId);

  const toggleLibraryRow = useCallback(
    (row) => {
      if (sameLibrary(expandedLibrary, row)) {
        setExpandedLibrary(null);
        return;
      }
      setLibPage(0);
      setLibLoading(true);
      setLibError(null);
      setLibRows([]);
      setLibTotal(0);
      setExpandedLibrary(row);
    },
    [expandedLibrary]
  );

  const toggleFolderRow = useCallback(
    (row) => {
      if (sameFolder(expandedFolder, row)) {
        setExpandedFolder(null);
        return;
      }
      setFoldPage(0);
      setFoldLoading(true);
      setFoldError(null);
      setFoldRows([]);
      setFoldTotal(0);
      setExpandedFolder(row);
    },
    [expandedFolder]
  );

  const libraryExpanded = (row) => sameLibrary(expandedLibrary, row);
  const folderExpanded = (row) => sameFolder(expandedFolder, row);

  const handleLibPageChange = (_, newPage) => {
    setLibPage(newPage);
  };

  const handleLibRowsPerPage = (e) => {
    const next = parseInt(e.target.value, 10);
    setLibPageSize(next);
    setLibPage(0);
  };

  const handleFoldPageChange = (_, newPage) => {
    setFoldPage(newPage);
  };

  const handleFoldRowsPerPage = (e) => {
    const next = parseInt(e.target.value, 10);
    setFoldPageSize(next);
    setFoldPage(0);
  };

  const siteRowSelected = (row) =>
    detailCtx?.scope === 'site' && detailCtx.configId === row.configId;

  const libTableColSpan = 1 + (showSiteColumnInLibrariesAndFolders ? 1 : 0) + 2;
  const foldTableColSpan = 1 + (showSiteColumnInLibrariesAndFolders ? 1 : 0) + 3;

  const showSitePicker = tab === 0 && scope.allSites;

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'rgba(244, 81, 30, 0.06)' }}>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          How to read this report
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
          <Typography component="li" variant="body2">
            <strong>Unique permissions</strong> are distinct principals (people and SharePoint groups) from{' '}
            <strong>library default security</strong> (counted once per library in scope) plus{' '}
            <strong>explicit item grants</strong>. Rows tagged <strong>[REMOVED]</strong> or <strong>DEFAULT</strong>{' '}
            are excluded. Principals that come from the library default list are shown as <strong>[Name] (D)</strong>. This
            is not your full directory count.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Sites</strong> (single-site scope): the table below lists every principal for that site. With{' '}
            <strong>All Sites</strong>, pick a site in the list, then review principals underneath.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Libraries</strong> and <strong>Folders</strong>: click a row to <strong>expand</strong> and load
            principals under that library or folder. The <strong>Folders</strong> list skips folders that only inherit
            library default security (no unique permissions on the folder).
          </Typography>
          <Typography component="li" variant="body2">
            The same principal can appear in many rows; <strong>do not add row counts</strong> and expect them to match
            the site total.
          </Typography>
          <Typography component="li" variant="body2">
            Tab labels like &quot;Libraries (20)&quot; refer to <strong>20 libraries</strong> (or listed folders), not 20
            principals.
          </Typography>
          {scope.allSites && (
            <Typography component="li" variant="body2">
              On Overview with <strong>All Sites</strong>, the Unique Permissions card is one combined count across
              sites; here, each site row still has its own count for that site only.
            </Typography>
          )}
        </Box>
      </Paper>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary">
            <Tab label={`Sites (${data.sites.length} site${data.sites.length === 1 ? '' : 's'})`} />
            <Tab
              label={`Libraries (${data.libraries.length} librar${data.libraries.length === 1 ? 'y' : 'ies'})`}
            />
            <Tab label={`Folders (${data.folders.length} folder${data.folders.length === 1 ? '' : 's'})`} />
          </Tabs>
          <TabPanel value={tab} index={0}>
            {showSitePicker && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: HEADER_BG }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Site</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                        Unique permissions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.sites.map((row) => (
                      <TableRow
                        key={row.configId}
                        hover
                        selected={siteRowSelected(row)}
                        onClick={() => selectSiteRow(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{row.siteLabel}</TableCell>
                        <TableCell align="right">
                          {Number(row.distinctUsers).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.sites.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2}>No data</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <PrincipalDetailBlock
              detailCtx={detailCtx}
              loading={detailLoading}
              error={detailError}
              rows={detailRows}
              total={detailTotal}
              page={detailPage}
              pageSize={detailPageSize}
              onPageChange={handleDetailPageChange}
              onRowsPerPageChange={handleDetailRowsPerPage}
              emptyHint={showSitePicker ? 'Select a site above to list principals.' : ''}
            />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: HEADER_BG }}>
                    <TableCell sx={{ width: 40, color: 'white', fontWeight: 'bold' }} aria-label="Expand" />
                    {showSiteColumnInLibrariesAndFolders && (
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Site</TableCell>
                    )}
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Library</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Unique permissions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.libraries.map((row, i) => (
                    <Fragment key={`${row.configId}-${row.libraryInternalId}-${i}`}>
                      <TableRow
                        hover
                        selected={libraryExpanded(row)}
                        onClick={() => toggleLibraryRow(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                          {libraryExpanded(row) ? (
                            <KeyboardArrowDownIcon fontSize="small" color="action" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" color="action" />
                          )}
                        </TableCell>
                        {showSiteColumnInLibrariesAndFolders && (
                          <TableCell>{row.siteLabel}</TableCell>
                        )}
                        <TableCell>{row.libraryName}</TableCell>
                        <TableCell align="right">{Number(row.distinctUsers).toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={libTableColSpan}
                          sx={{
                            py: 0,
                            px: 0,
                            borderBottom: libraryExpanded(row) ? undefined : 0,
                            bgcolor: 'grey.50'
                          }}
                        >
                          <Collapse in={libraryExpanded(row)} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: 1.5 }}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Principals
                              </Typography>
                              <PrincipalsTableBody
                                loading={libLoading}
                                error={libError}
                                rows={libRows}
                                total={libTotal}
                                page={libPage}
                                pageSize={libPageSize}
                                onPageChange={handleLibPageChange}
                                onRowsPerPageChange={handleLibRowsPerPage}
                                embedded
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
                  {data.libraries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={libTableColSpan}>No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: HEADER_BG }}>
                    <TableCell
                      sx={{ width: 40, color: 'white', fontWeight: 'bold', backgroundColor: HEADER_BG }}
                      aria-label="Expand"
                    />
                    {showSiteColumnInLibrariesAndFolders && (
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: HEADER_BG }}>
                        Site
                      </TableCell>
                    )}
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: HEADER_BG }}>Library</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: HEADER_BG }}>
                      Folder path
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: 'white', fontWeight: 'bold', backgroundColor: HEADER_BG }}
                    >
                      Unique permissions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.folders.map((row, i) => (
                    <Fragment key={`${row.configId}-${row.folderItemId}-${i}`}>
                      <TableRow
                        hover
                        selected={folderExpanded(row)}
                        onClick={() => toggleFolderRow(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                          {folderExpanded(row) ? (
                            <KeyboardArrowDownIcon fontSize="small" color="action" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" color="action" />
                          )}
                        </TableCell>
                        {showSiteColumnInLibrariesAndFolders && (
                          <TableCell>{row.siteLabel}</TableCell>
                        )}
                        <TableCell>{row.libraryName}</TableCell>
                        <TableCell sx={{ maxWidth: 360, wordBreak: 'break-word' }}>{row.folderPath}</TableCell>
                        <TableCell align="right">{Number(row.distinctUsers).toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={foldTableColSpan}
                          sx={{
                            py: 0,
                            px: 0,
                            borderBottom: folderExpanded(row) ? undefined : 0,
                            bgcolor: 'grey.50'
                          }}
                        >
                          <Collapse in={folderExpanded(row)} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: 1.5 }}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Principals
                              </Typography>
                              <PrincipalsTableBody
                                loading={foldLoading}
                                error={foldError}
                                rows={foldRows}
                                total={foldTotal}
                                page={foldPage}
                                pageSize={foldPageSize}
                                onPageChange={handleFoldPageChange}
                                onRowsPerPageChange={handleFoldRowsPerPage}
                                embedded
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
                  {data.folders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={foldTableColSpan}>No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default PermissionScopeReport;
